import React, { useRef, useState } from 'react'

import styled from '@emotion/styled'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import LoadingIcon from '../../LoadingIcon/LoadingIcon'
import ArcadeHelpText, { ArcadeHelpTextComponentProps, combineAriaDescribedBy } from '../Input/ArcadeHelpText'

const SWIPE_DISTANCE = 15

const StyledToggle = styled.div`
  display: inline-block;
  position: relative;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 600ms;

  &.react-toggle {
    touch-action: pan-y;
  }

  &.react-toggle--disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* stylelint-disable no-descending-specificity -- legacy CSS targetting a library */
  .react-toggle-track,
  &.react-toggle:hover .react-toggle-track {
    width: 3.25rem;
    height: 2rem;
    padding: 0;
    border-radius: 30px;
    background: ${({ theme }) => theme.arcadeColors.component.toggleInactiveBackgroundDefault};
    transition: all 0.2s ease;
  }

  .react-toggle-thumb {
    position: absolute;
    box-sizing: border-box;
    top: 0.125rem;
    left: 0.125rem;
    height: 1.75rem;
    width: 1.75rem;
    border: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.arcadeColors.component.toggleKnobBackgroundDefault};
    transition: all 0.25s ease;
  }

  &.react-toggle--checked .react-toggle-track,
  &.react-toggle--checked:hover .react-toggle-track {
    background: ${({ theme }) => theme.arcadeColors.component.toggleActiveBackgroundDefault};
  }

  &.react-toggle--checked .react-toggle-thumb {
    left: 1.375rem;
  }

  &.react-toggle--disabled .react-toggle-track,
  &.react-toggle--disabled:hover .react-toggle-track {
    background: ${({ theme }) => theme.arcadeColors.component.toggleInactiveBackgroundDisabled};
  }

  &.react-toggle--disabled .react-toggle-thumb {
    background: ${({ theme }) => theme.arcadeColors.component.toggleKnobBackgroundDisabled};
  }

  &.react-toggle--disabled.react-toggle--checked .react-toggle-track {
    background: ${({ theme }) => theme.arcadeColors.component.toggleActiveBackgroundDisabled};
  }

  &.react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb,
  &.react-toggle--focus .react-toggle-thumb {
    box-shadow: none;
  }

  ${props => props.theme.selectors.keyboard} &.react-toggle--focus .react-toggle-track {
    outline: ${({ theme }) => `0.125rem solid ${theme.arcadeColors.component.inputBorderFocus}`};
  }

  .react-toggle-screenreader-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    border: 0;
    white-space: nowrap;
  }
  /* stylelint-enable no-descending-specificity */
`
const ToggleContainer = styled.div`
  position: relative;
  height: 2rem;
`

const ToggleLoadingIcon = styled.div<{
  checked?: boolean
}>`
  position: absolute;
  pointer-events: none;
  transition: all 0.2s ease;
  top: 0;
  margin-left: ${({ checked }) => (checked ? '1.375rem' : '0.125rem')};
  margin-top: 0.125rem;
`

export interface ArcadeToggleProps extends React.InputHTMLAttributes<HTMLInputElement>, ArcadeHelpTextComponentProps {
  'data-testid'?: string
  isLoading?: boolean
}

const ArcadeToggle = (props: ArcadeToggleProps) => {
  const {
    checked,
    defaultChecked,
    className,
    disabled = false,
    isLoading,
    onBlur,
    onChange,
    onClick,
    onFocus,
    'aria-describedby': ariaDescribedBy,
    'data-testid': dataTestId,
    helpText,
    HelpTextIcon,
    isInvalid,
    errorText,
    disabledText,
    marginTop,
    ...inputProps
  } = props
  const helpTextProps = {
    helpText,
    HelpTextIcon,
    isInvalid,
    errorText,
    disabled,
    disabledText,
    marginTop,
  }
  const helpTextId = useHelpTextId(helpTextProps)
  const inputRef = useRef<HTMLInputElement>(null)
  const gesture = useRef<{ pointerId: number; x: number; y: number; moved: boolean } | null>(null)
  const suppressClick = useRef(false)
  const [uncontrolledChecked, setUncontrolledChecked] = useState(!!defaultChecked)
  const [dragChecked, setDragChecked] = useState<boolean | null>(null)
  const [hasFocus, setHasFocus] = useState(false)
  const isChecked = checked ?? uncontrolledChecked
  const displayedChecked = dragChecked ?? isChecked
  const isInert = disabled || isLoading

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    suppressClick.current = false
    if (isInert || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    gesture.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moved: false,
    }
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = gesture.current
    if (isInert || !start || start.pointerId !== event.pointerId) return
    const distanceX = event.clientX - start.x
    const distanceY = event.clientY - start.y
    if (Math.abs(distanceX) >= SWIPE_DISTANCE && Math.abs(distanceX) > Math.abs(distanceY)) {
      start.moved = true
      setDragChecked(distanceX > 0)
    } else {
      setDragChecked(null)
    }
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = gesture.current
    if (isInert || !start || start.pointerId !== event.pointerId) return
    gesture.current = null
    const distanceX = event.clientX - start.x
    const distanceY = event.clientY - start.y
    const swiped = Math.abs(distanceX) >= SWIPE_DISTANCE && Math.abs(distanceX) > Math.abs(distanceY)
    setDragChecked(null)
    const dragged = Math.hypot(distanceX, distanceY) >= SWIPE_DISTANCE
    if (!start.moved && !dragged) return

    suppressClick.current = true
    event.preventDefault()
    const nextChecked = distanceX > 0
    if (swiped && nextChecked !== isChecked) {
      inputRef.current?.focus()
      inputRef.current?.click()
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === inputRef.current) return
    if (suppressClick.current) {
      suppressClick.current = false
      event.preventDefault()
      return
    }
    if (isInert) return
    event.preventDefault()
    inputRef.current?.focus()
    inputRef.current?.click()
  }

  const resetGesture = () => {
    gesture.current = null
    setDragChecked(null)
  }

  const toggleClasses = [
    'react-toggle',
    displayedChecked && 'react-toggle--checked',
    hasFocus && 'react-toggle--focus',
    disabled && 'react-toggle--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <ToggleContainer>
        <StyledToggle
          className={toggleClasses}
          onClick={handleClick}
          onLostPointerCapture={resetGesture}
          onPointerCancel={resetGesture}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div className="react-toggle-track" />
          <div className="react-toggle-thumb" />
          <input
            {...inputProps}
            ref={inputRef}
            type="checkbox"
            className="react-toggle-screenreader-only"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            aria-disabled={isLoading || inputProps['aria-disabled']}
            data-testid={dataTestId ?? 'styled-toggle'}
            aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
            onClick={event => {
              if (isLoading) {
                event.preventDefault()
                return
              }
              onClick?.(event)
            }}
            onChange={event => {
              if (isLoading) return
              if (checked === undefined) setUncontrolledChecked(event.currentTarget.checked)
              onChange?.(event)
            }}
            onFocus={event => {
              setHasFocus(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              setHasFocus(false)
              onBlur?.(event)
            }}
          />
        </StyledToggle>
        {isLoading ? (
          <ToggleLoadingIcon checked={displayedChecked}>
            <LoadingIcon size={28} />
          </ToggleLoadingIcon>
        ) : null}
      </ToggleContainer>
      <ArcadeHelpText helpTextId={helpTextId} {...helpTextProps} />
    </>
  )
}

export default ArcadeToggle
